import { useState } from 'react'
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { colors } from '../../theme'
import { ErrorIcon } from './ErrorIcon'

interface FullScreenErrorWallProps {
  message: string
  title: string
  primaryActionLabel: string
  onPrimaryAction: () => unknown | Promise<unknown>
  secondaryActionLabel?: string
  onSecondaryAction?: () => unknown | Promise<unknown>
}

const ICON_HALO_SIZE = 96
const ICON_SIZE = 44

export function FullScreenErrorWall({
  message,
  title,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
}: FullScreenErrorWallProps) {
  const insets = useSafeAreaInsets()
  const [pendingAction, setPendingAction] = useState<
    'primary' | 'secondary' | null
  >(null)

  const isPrimaryPending = pendingAction === 'primary'
  const isSecondaryPending = pendingAction === 'secondary'
  const isBusy = pendingAction != null
  const showsSecondaryAction =
    secondaryActionLabel != null && onSecondaryAction != null

  const handlePrimaryAction = () => {
    if (isBusy) {
      return
    }

    setPendingAction('primary')
    void Promise.resolve(onPrimaryAction())
      .catch(() => undefined)
      .finally(() => {
        setPendingAction((current) => (current === 'primary' ? null : current))
      })
  }

  const handleSecondaryAction = () => {
    if (isBusy || onSecondaryAction == null) {
      return
    }

    setPendingAction('secondary')
    void Promise.resolve(onSecondaryAction())
      .catch(() => undefined)
      .finally(() => {
        setPendingAction((current) =>
          current === 'secondary' ? null : current,
        )
      })
  }

  return (
    <View
      className="flex-1 bg-background-primary"
      style={{
        paddingTop: insets.top,
        paddingBottom: insets.bottom,
        paddingLeft: insets.left,
        paddingRight: insets.right,
      }}
    >
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: 24,
          paddingVertical: 32,
        }}
        showsVerticalScrollIndicator={false}
        bounces={false}
      >
        <View className="w-full max-w-md items-center">
          <View
            className="items-center justify-center rounded-full bg-error-background"
            style={{ width: ICON_HALO_SIZE, height: ICON_HALO_SIZE }}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <ErrorIcon size={ICON_SIZE} color={colors.destructive} />
          </View>

          <Text
            className="text-headline-1 text-ink text-center mt-6"
            accessibilityRole="header"
          >
            {title}
          </Text>

          <Text className="text-body-2 text-ink-secondary text-center mt-3 leading-6">
            {message}
          </Text>

          <View className="w-full mt-10">
            <Pressable
              className={`w-full py-4 rounded-2xl items-center justify-center bg-primary ${
                isBusy ? 'opacity-70' : ''
              }`}
              onPress={handlePrimaryAction}
              disabled={isBusy}
              accessibilityRole="button"
              accessibilityState={{
                disabled: isBusy,
                busy: isPrimaryPending,
              }}
              accessibilityLabel={primaryActionLabel}
            >
              {isPrimaryPending ? (
                <ActivityIndicator color={colors.primaryForeground} />
              ) : (
                <Text className="text-label-1 text-primary-foreground">
                  {primaryActionLabel}
                </Text>
              )}
            </Pressable>

            {showsSecondaryAction ? (
              <Pressable
                className={`w-full py-4 rounded-2xl items-center justify-center mt-3 border border-border bg-background ${
                  isBusy ? 'opacity-70' : ''
                }`}
                onPress={handleSecondaryAction}
                disabled={isBusy}
                accessibilityRole="button"
                accessibilityState={{
                  disabled: isBusy,
                  busy: isSecondaryPending,
                }}
                accessibilityLabel={secondaryActionLabel}
              >
                {isSecondaryPending ? (
                  <ActivityIndicator color={colors.ink} />
                ) : (
                  <Text className="text-label-1 text-ink">
                    {secondaryActionLabel}
                  </Text>
                )}
              </Pressable>
            ) : null}
          </View>
        </View>
      </ScrollView>
    </View>
  )
}
